import { formatHeaderName } from './columnLabels.js';

/** Reader copy never changes the column used to filter the data. */
export function selectionColumnLabel(entry = {}) {
  if (entry.filterColumnLabel) return entry.filterColumnLabel;
  if (entry.filterColumn === 'MasterTagName') return 'Equipment';
  return formatHeaderName(entry.filterColumn || 'Selection');
}

export function metricIsKnown(value) {
  if (value == null || typeof value === 'boolean') return false;
  if (typeof value === 'string' && !value.trim()) return false;
  return Number.isFinite(Number(value));
}

const readerNumberFormat = new Intl.NumberFormat('en', { maximumFractionDigits: 2 });
export function formatDashboardNumber(value) {
  if (!metricIsKnown(value)) return 'Not available';
  const numeric = Number(value);
  if (numeric > 0 && numeric < 0.01) return '<0.01';
  if (numeric < 0 && numeric > -0.01) return '>-0.01';
  return readerNumberFormat.format(numeric);
}

export function spatialTargetLabel(result = {}) {
  const keys = new Set((result.occurrences || []).flatMap(occurrence => (
    occurrence.bindings || []).filter(binding => binding.role === 'target').map(binding => binding.subjectKey)));
  const subjects = (result.subjects || []).filter(subject => keys.has(subject.subjectKey));
  if (subjects.length && subjects.every(subject => subject.kind === 'cml')) return 'inspection points';
  if (subjects.length && subjects.every(subject => subject.isMaster === true)) return 'equipment items';
  return 'linked items';
}

export function hasDisplayTarget(result = {}) {
  const addresses = new Map((result.addresses || []).map(address => [address.addressKey, address]));
  return (result.occurrences || []).some(occurrence => (occurrence.bindings || []).some(binding => (
    binding.role === 'target' && (binding.addressKeys || []).some(key => {
      const address = addresses.get(key);
      if (!address || (address.state && address.state !== 'resolved')) return false;
      if (address.kind === 'elements') return Array.isArray(address.dbIds) && address.dbIds.length > 0;
      return address.kind === 'point' && ['x', 'y', 'z'].every(axis => Number.isFinite(address.point?.[axis]));
    })
  )));
}

/** Explicit related-equipment action, never an implicit fallback for a missing record location. */
export function relatedEquipmentResult(result) {
  const equipment = new Set((result.subjects || []).filter(subject => subject.isMaster === true).map(subject => subject.subjectKey));
  return {
    ...result,
    occurrences: (result.occurrences || []).map(occurrence => ({
      ...occurrence,
      bindings: (occurrence.bindings || []).filter(binding => binding.role === 'context' && equipment.has(binding.subjectKey))
        .map(binding => ({ ...binding, role: 'target' })),
    })),
  };
}

export function filterConditionLabel(filter, label) {
  const field = label && label !== filter.column
    ? label.replace(/^./, first => first.toUpperCase()) : formatHeaderName(filter.column);
  const operator = {
    equals: 'is', not_equals: 'is not', in: 'is one of', not_in: 'is not one of',
    contains: 'contains', not_contains: 'does not contain', starts_with: 'starts with',
    gt: 'is greater than', gte: 'is at least', lt: 'is less than', lte: 'is at most',
    in_or_null: 'is not set or is one of',
    greater_than: 'is greater than', greater_than_or_equal: 'is at least',
    less_than: 'is less than', less_than_or_equal: 'is at most',
    is_null: 'is not set', is_not_null: 'is set',
  }[filter.operator] || formatHeaderName(filter.operator || 'matches').toLowerCase();
  let rawValue = filter.value;
  if (typeof rawValue === 'string' && /^\s*\[/.test(rawValue)) {
    try {
      const parsed = JSON.parse(rawValue);
      if (Array.isArray(parsed)) rawValue = parsed;
    } catch (err) { /* A literal value may contain an opening bracket. */ }
  }
  const values = Array.isArray(rawValue) ? rawValue : [rawValue];
  const knownValues = values.filter(value => value != null);
  if (knownValues.length > 5 && ['in', 'not_in', 'in_or_null'].includes(filter.operator)) {
    return `${field} ${operator} ${knownValues.length} selected values`;
  }
  const text = knownValues.map(value => {
    if (value === true || value === 'true') return 'Yes';
    if (value === false || value === 'false') return 'No';
    if (typeof value === 'object') return 'value unavailable';
    return String(value);
  }).join(filter.operator === 'between' ? ' and ' : ' or ');
  return `${field} ${operator}${text ? ` ${text}` : ''}`;
}

export function plainDashboardCopy(value) {
  const text = String(value || '').trim();
  const known = {
    'Native execution state': 'Based on the recorded work status',
    'Native scheduled execution state': 'Work recorded as scheduled',
    'Explicit open findings': 'Anomalies recorded as open',
    'Explicit blocked workflow state': 'Actions marked as blocked',
    'Scope rows, not distinct work-order numbers': 'One work order can include several work scopes',
    'Work scopes, not distinct WO numbers': 'One work order can include several work scopes',
    'TAR Scope = Yes': 'Work included in the planned turnaround',
    'Known current values only': 'Uses available estimates; missing values are excluded',
    'Completion evidence on TAR scope IDs': 'Required quality records for turnaround work',
  };
  if (known[text]) return known[text];
  if (/^\d+ superseded locations excluded$/i.test(text)) return 'Replaced inspection points are excluded';
  return text;
}

/** Display a typed outcome; raw service errors are not reader-facing copy. */
export function spatialRefusalMessage(reason) {
  if (reason === 'spatialNoDisplayTargets') return 'No filtered records have a location available in this 3D view.';
  if (reason === 'spatialCapabilityMissing') return '3D locations are not available for this data source. You can still view its records and values.';
  if (reason === 'spatialGlobalFiltersUnsupported') return 'The current filters are still being prepared for the 3D view. Please refresh the dashboard and try again.';
  if (reason === 'spatialRowKeyMissing') return 'This record could not be identified for the 3D view. Please refresh the dashboard and try again.';
  return 'The selected records could not be checked for 3D locations. Please refresh the dashboard and try again.';
}

export function focusOutcomeMessage(outcome) {
  if (!outcome) return '';
  if (outcome.status === 'empty') return 'No records match this selection. Adjust the filters to see other records.';
  if (outcome.status === 'failed') return 'The 3D view could not be opened. Please try again.';
  if (outcome.status !== 'unrenderable') return '';
  const codes = new Set((outcome.diagnostics || []).filter(item => item.count !== 0).map(item => item.code));
  if (codes.has('modelMismatch') && codes.has('subjectWithoutGeometry')) {
    return 'Some locations are in another model, and some records have no available 3D location. These records remain included in the dashboard values.';
  }
  if (codes.has('modelMismatch')) {
    return 'This selection includes locations in another model. Open the relevant model to view them.';
  }
  if (codes.has('subjectWithoutGeometry')) {
    return 'The selected records do not have a location that can be shown in this 3D view. They remain included in the dashboard values.';
  }
  return 'The selected locations could not be displayed in this 3D view. These records remain included in the dashboard values.';
}
